import { Head, Link, router, usePage } from '@inertiajs/react';
import { ReactNode } from 'react';
import { useSidebarState } from '@/components/CollapsibleSidebar';

/**
 * Shared page props from Inertia.
 */
interface SharedPageProps {
    unansweredQuestionsCount?: number;
    [key: string]: unknown;
}

/**
 * Navigation item for the GM sidebar.
 */
interface NavItem {
    /** Display label */
    label: string;
    /** Route path */
    href: string;
    /** Icon emoji */
    icon: string;
}

/**
 * Props for the GmLayout component.
 */
interface GmLayoutProps {
    /** Page title for the browser tab */
    title: string;
    /** Currently active navigation item path */
    activePath: string;
    /** Main content to render */
    children: ReactNode;
}

/**
 * Navigation items for the GM sidebar.
 */
/**
 * Navigation items for the GM sidebar.
 * Tome now points to /gm/tome/books (book list) instead of /gm/tome (page list).
 */
const NAV_ITEMS: NavItem[] = [
    { label: 'Dashboard', href: '/gm', icon: '📊' },
    { label: 'Images', href: '/gm/images', icon: '🖼️' },
    { label: 'Users', href: '/gm/users', icon: '👥' },
    { label: 'Items', href: '/gm/items', icon: '🎒' },
    { label: 'Adventures', href: '/gm/adventures', icon: '⚔️' },
    { label: 'Global Challenges', href: '/gm/global-challenges', icon: '🌍' },
    { label: 'Tome', href: '/gm/tome/books', icon: '📖' },
    { label: 'University', href: '/gm/university', icon: '🎓' },
    { label: 'Newspaper', href: '/gm/newspaper', icon: '📰' },
    { label: 'Missive Reports', href: '/gm/missive-reports', icon: '🚩' },
    { label: 'Game Log', href: '/gm/game-log', icon: '📋' },
];

/**
 * GmLayout - Layout component for GM management pages.
 * Provides a consistent header, collapsible sidebar navigation, and content area.
 * Sidebar is hidden by default on mobile and visible on desktop.
 */
export default function GmLayout({ title, activePath, children }: GmLayoutProps) {
    // Use responsive sidebar state - hidden by default on mobile, visible on desktop
    const { isOpen: sidebarOpen, isMobile, toggle: toggleSidebar, close: closeSidebar } = useSidebarState(true, false);

    // Get unanswered questions count from shared page props
    const { unansweredQuestionsCount = 0 } = usePage<SharedPageProps>().props;

    return (
        <>
            <Head title={`GM - ${title}`} />

            <div className="min-h-screen bg-slate-900 flex flex-col">
                {/* Header */}
                <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Mobile sidebar toggle in header */}
                            <button
                                onClick={toggleSidebar}
                                className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
                                aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                                aria-expanded={sidebarOpen}
                            >
                                {/* Hamburger / X icon */}
                                <span className="block w-5 h-5 relative">
                                    <span
                                        className={`
                                            absolute left-0 w-5 h-0.5 bg-current
                                            transition-all duration-300
                                            ${sidebarOpen ? 'top-2 rotate-45' : 'top-1'}
                                        `}
                                    />
                                    <span
                                        className={`
                                            absolute left-0 top-2 w-5 h-0.5 bg-current
                                            transition-all duration-300
                                            ${sidebarOpen ? 'opacity-0' : 'opacity-100'}
                                        `}
                                    />
                                    <span
                                        className={`
                                            absolute left-0 w-5 h-0.5 bg-current
                                            transition-all duration-300
                                            ${sidebarOpen ? 'top-2 -rotate-45' : 'top-3'}
                                        `}
                                    />
                                </span>
                            </button>
                            <Link
                                href="/map"
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                ← Back to Map
                            </Link>
                            <h1 className="text-xl font-bold text-white hidden sm:block">GM Management</h1>
                        </div>
                        <nav className="flex items-center gap-4">
                            <button
                                type="button"
                                onClick={() => router.post('/logout')}
                                className="text-gray-300 hover:text-white transition-colors"
                            >
                                Logout
                            </button>
                        </nav>
                    </div>
                </header>

                {/* Main content area with sidebar */}
                <div className="flex-1 flex overflow-hidden relative">
                    {/* Mobile overlay backdrop - shown when sidebar is open on mobile */}
                    {isMobile && sidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black/50 z-40 md:hidden"
                            onClick={closeSidebar}
                            aria-hidden="true"
                        />
                    )}

                    {/* Left Sidebar - Navigation */}
                    <aside
                        className={`
                            ${sidebarOpen ? 'w-64' : 'w-0'}
                            bg-slate-800 border-r border-slate-700
                            flex-shrink-0 overflow-hidden
                            transition-all duration-300 ease-in-out
                            ${isMobile ? 'fixed inset-y-0 left-0 z-50 top-[57px]' : 'relative'}
                        `}
                    >
                        <div className="w-64 h-full overflow-y-auto">
                            <nav className="p-4">
                                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                    GM Tools
                                </h2>
                                <ul className="space-y-1">
                                    {NAV_ITEMS.map((item) => {
                                        // Use startsWith for Tome to highlight on all tome sub-pages
                                        // For other items, use exact match or startsWith based on path structure
                                        const isActive = item.href === '/gm'
                                            ? activePath === '/gm'
                                            : activePath.startsWith(item.href);
                                        return (
                                            <li key={item.href}>
                                                <Link
                                                    href={item.href}
                                                    onClick={() => isMobile && closeSidebar()}
                                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                                                        isActive
                                                            ? 'bg-purple-600 text-white'
                                                            : 'text-gray-300 hover:bg-slate-700 hover:text-white'
                                                    }`}
                                                >
                                                    <span className="text-lg">{item.icon}</span>
                                                    <span className="flex-1">{item.label}</span>
                                                    {item.href === '/gm/newspaper' && unansweredQuestionsCount > 0 && (
                                                        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                                                            {unansweredQuestionsCount > 99 ? '99+' : unansweredQuestionsCount}
                                                        </span>
                                                    )}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        </div>
                    </aside>

                    {/* Desktop sidebar toggle button - positioned at edge of sidebar */}
                    <button
                        onClick={toggleSidebar}
                        className={`
                            hidden md:flex
                            absolute top-4 z-30
                            p-2 bg-slate-700 border border-slate-600 rounded-lg
                            text-gray-300 hover:text-white hover:bg-slate-600
                            transition-all duration-300
                            focus:outline-none focus:ring-2 focus:ring-purple-500
                            items-center justify-center
                        `}
                        style={{ left: sidebarOpen ? 'calc(16rem - 1.25rem)' : '0.5rem' }}
                        aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                        aria-expanded={sidebarOpen}
                    >
                        <span className="text-sm">{sidebarOpen ? '◀' : '▶'}</span>
                    </button>

                    {/* Right Content Area */}
                    <main className="flex-1 overflow-y-auto p-6">
                        {children}
                    </main>
                </div>
            </div>
        </>
    );
}
